import { useCallback } from 'react';
import { useUIStore } from '../stores/uiStore';
import type { Execution } from '../types/api';

/**
 * Return type for useExecutionFilters hook
 */
interface UseExecutionFiltersReturn {
  filters: {
    search: string;
    status: Execution['status'] | '';
    flowId: string;
    dateRange: { start: string; end: string };
    showFilters: boolean;
  };
  handleSearchChange: (value: string) => void;
  handleStatusChange: (status: Execution['status'] | '') => void;
  handleFlowFilterChange: (flowId: string) => void;
  handleDateRangeChange: (field: 'start' | 'end', value: string) => void;
  toggleFilters: () => void;
  resetFilters: () => void;
  filterExecutions: (executions: Execution[]) => Execution[];
}

/**
 * Custom hook for managing execution filters and search functionality
 */
export function useExecutionFilters(): UseExecutionFiltersReturn {
  const {
    executionFilters,
    setExecutionFilters,
  } = useUIStore();

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((value: string) => {
    setExecutionFilters({ search: value });
  }, [setExecutionFilters]);

  /**
   * Handle status filter change
   */
  const handleStatusChange = useCallback((status: Execution['status'] | '') => {
    setExecutionFilters({ status });
  }, [setExecutionFilters]);

  /**
   * Handle flow ID filter change
   */
  const handleFlowFilterChange = useCallback((flowId: string) => {
    setExecutionFilters({ flowId });
  }, [setExecutionFilters]);

  /**
   * Handle date range changes
   */
  const handleDateRangeChange = useCallback((field: 'start' | 'end', value: string) => {
    setExecutionFilters({
      dateRange: {
        ...executionFilters.dateRange,
        [field]: value,
      },
    });
  }, [executionFilters.dateRange, setExecutionFilters]);

  /**
   * Toggle filters visibility
   */
  const toggleFilters = useCallback(() => {
    setExecutionFilters({ showFilters: !executionFilters.showFilters });
  }, [executionFilters.showFilters, setExecutionFilters]);

  /**
   * Reset all filters
   */
  const resetFilters = useCallback(() => {
    setExecutionFilters({
      search: '',
      status: '',
      flowId: '',
      dateRange: { start: '', end: '' },
    });
  }, [setExecutionFilters]);

  /**
   * Filter executions based on search term (client-side)
   */
  const filterExecutions = useCallback((executions: Execution[]) => {
    return executions.filter(execution => 
      execution.flowName.toLowerCase().includes(executionFilters.search.toLowerCase()) ||
      execution.id.toLowerCase().includes(executionFilters.search.toLowerCase())
    );
  }, [executionFilters.search]);

  return {
    filters: executionFilters,
    handleSearchChange,
    handleStatusChange,
    handleFlowFilterChange,
    handleDateRangeChange,
    toggleFilters,
    resetFilters,
    filterExecutions,
  };
} 